import { ArrowUpRight, Building2, FileText, Globe, PlayCircle, SearchX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { MicrositeNav } from "@/components/microsite/MicrositeNav";
import { eventBase } from "@/lib/microsite";
import { ExternalRecordDialog } from "@/components/ExternalRecordDialog";
import { FavoriteButton } from "@/components/FavoriteButton";
import { HorseLotDock } from "@/components/bidding/HorseLotDock";
import { Lightbox } from "@/components/media/Lightbox";
import { VideoStage } from "@/components/media/VideoStage";
import { VetRepository } from "@/components/VetRepository";
import { useUserSocket } from "@/hooks/useSockets";
import { PedigreeTree } from "@/components/PedigreeTree";
import { PerformanceRelatives } from "@/components/PerformanceRelatives";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, mediaUrlFromMap } from "@/lib/api";
import { localized } from "@/lib/intl";
import { usePageMeta } from "@/lib/seo";
import { brandStyle } from "@/lib/theme";
import { useFavoriteIds } from "@/hooks/useFavoriteIds";
import { BRAND } from "@/config/brand";

const GENETIC_TYPES = ["embryo", "breeding_right", "semen_dose"];
const YOUNG_TYPES = ["foal", "yearling", "young_horse"];

/* Which clip categories lead, per asset type — "a horse is sold with video". */
const CATEGORY_ORDER = {
  sport: ["competition", "jumping", "dressage", "training", "liberty", "vet_inspection", "other"],
  young: ["liberty", "training", "jumping", "dressage", "competition", "vet_inspection", "other"],
};

const Fact = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/40">{label}</span>
      <span className="text-sm font-medium text-[#0A0A0A]">{value}</span>
    </div>
  );
};

export default function HorseProfile() {
  const { horseId } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(undefined);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [recordLink, setRecordLink] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const stageRef = useRef(null);
  const favIds = useFavoriteIds();

  useEffect(() => {
    setData(undefined);
    api
      .get(`/public/horses/${horseId}`)
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null));
  }, [horseId]);

  /* Vet repository real-time unlock: silently refetch when the registration
     approval notification lands (or after in-page registration). */
  const reload = () =>
    api.get(`/public/horses/${horseId}`).then(({ data: payload }) => setData(payload)).catch(() => {});
  useUserSocket((msg) => {
    if (msg?.type === "notification") reload();
  });

  const locale = i18n.resolvedLanguage;
  // WS6: caballos sin contenido mínimo (ni media ni descripción) → noindex.
  const hasMinContent = Boolean(
    (data?.horse?.media || []).length || localized(data?.horse?.description, locale)
  );
  usePageMeta({
    title: data?.horse ? `${data.horse.name} — ${BRAND.name}` : undefined,
    description: data?.horse ? localized(data.horse.description, locale) : undefined,
    image: data?.horse ? mediaUrlFromMap(data.media, data.horse.cover_media_id) : undefined,
    imageAlt: data?.horse?.name,
    robots: data?.horse && !hasMinContent ? "noindex, follow" : undefined,
  });

  if (data === undefined) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 flex flex-col gap-10" data-testid="horse-profile-skeleton">
        <Skeleton className="w-full aspect-video rounded-3xl" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="flex flex-col gap-4">
            <Skeleton className="h-12 w-2/3 rounded-xl" />
            <Skeleton className="h-32 rounded-2xl" />
          </div>
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <EmptyState icon={SearchX} testid="horse-not-found" title={t("pub.emptyCatalogTitle")} description={t("pub.emptyCatalogDesc")} />
      </div>
    );
  }

  const { horse, organizer, media, events } = data;
  const genetic = GENETIC_TYPES.includes(horse.asset_type);
  const young = YOUNG_TYPES.includes(horse.asset_type);
  const cover = mediaUrlFromMap(media, horse.cover_media_id);

  /* ---------- video derivation (adaptive by asset type) ---------- */
  const sireIds = horse.sire_info?.video_media_ids || [];
  const damIds = horse.dam_info?.video_media_ids || [];
  const bloodlineIds = new Set([...sireIds, ...damIds]);
  const order = CATEGORY_ORDER[young ? "young" : "sport"];
  const rank = (c) => {
    const i = order.indexOf(c || "other");
    return i < 0 ? 99 : i;
  };
  const byCategory = (arr) => [...arr].sort((a, b) => rank(a.category) - rank(b.category));

  /* own footage follows the organizer's manual order (video_ids), starred main first;
     legacy assets missing from video_ids fall back to category ranking */
  const vidOrder = horse.video_ids || [];
  const pos = (m) => {
    if (m.id === horse.main_video_id) return -1;
    const i = vidOrder.indexOf(m.id);
    return i < 0 ? 500 + rank(m.category) : i;
  };
  const ownVideos = Object.values(media)
    .filter((m) => m.kind === "video" && !bloodlineIds.has(m.id))
    .sort((a, b) => pos(a) - pos(b));
  const sireVideos = byCategory(sireIds.map((id) => media[id]).filter(Boolean)).map((m) => ({ ...m, label: horse.sire_info?.name, bloodline: "sire" }));
  const damVideos = byCategory(damIds.map((id) => media[id]).filter(Boolean)).map((m) => ({ ...m, label: horse.dam_info?.name, bloodline: "dam" }));

  // embryo & genetic assets NEVER carry own footage — the bloodline is the story
  const stageVideos = genetic ? [...sireVideos, ...damVideos] : [...ownVideos, ...sireVideos, ...damVideos];

  /* ---------- photo gallery (cover first, deduped) ---------- */
  const photoIds = [horse.cover_media_id, ...horse.media_ids, ...(data.linked_media_ids || [])];
  const seen = new Set();
  const photos = photoIds
    .map((id) => media[id])
    .filter((m) => {
      if (!m || m.kind !== "photo" || seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    })
    .map((m) => ({ url: m.url, alt: horse.name }));

  const videoFirst = stageVideos.length > 0;

  /* ---------- documentation split (Phase 3.5): general docs vs Veterinary Repository ---------- */
  const vetIdSet = new Set(horse.vet_report_media_ids || []);
  const documents = Object.values(media).filter(
    (m) => (m.kind === "document" || m.kind === "xray" || m.kind === "certificate") && !vetIdSet.has(m.id) && !m.vet_category
  );
  const vetDocs = Object.values(media).filter((m) => vetIdSet.has(m.id) || m.vet_category);

  const jumpToBloodline = (which) => {
    setStageFilter(which);
    stageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const titleBlock = (
    <div className="flex flex-col gap-3">
      {(horse.disciplines?.length > 0 || horse.asset_type !== "sport_horse") && (
        <div className="flex flex-wrap gap-2">
          {horse.asset_type !== "sport_horse" && (
            <Badge data-testid="horse-asset-type-badge" className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] border-0">
              {horse.asset_type === "embryo" && horse.embryo_state
                ? `${t("asset.embryo")} • ${t(`horse.${horse.embryo_state}Short`)}`
                : t(`asset.${horse.asset_type}`)}
            </Badge>
          )}
          {(horse.disciplines || []).map((d) => (
            <Badge key={d} data-testid={`horse-discipline-${d}`} className="bg-[#1D4D3B]/10 text-[#1D4D3B] hover:bg-[#1D4D3B]/10 border-0">
              {t(`disc.${d}`, { defaultValue: d })}
            </Badge>
          ))}
        </div>
      )}
      <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-[#0A0A0A]" data-testid="horse-name">
        {horse.name}
      </h1>
      {organizer && (
        <span className="inline-flex items-center gap-2 text-sm text-[#737373]">
          <Building2 className="w-4 h-4" aria-hidden="true" />
          {t("pub.by", { name: organizer.name })}
        </span>
      )}
    </div>
  );

  const factsGrid = (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-5 bg-[#F9F9F9] rounded-2xl border border-black/5 p-6">
      <Fact label={t("pub.born")} value={horse.year_of_birth} />
      <Fact label={t("pub.sexLabel")} value={horse.sex && t(`horse.${horse.sex}`)} />
      <Fact label={t("pub.studbook")} value={horse.studbook} />
      <Fact label={t("pub.colorLabel")} value={horse.color} />
      <Fact label={t("pub.height")} value={horse.height_cm && `${horse.height_cm} cm`} />
      <Fact
        label={t("pub.expectedBirth")}
        value={
          horse.expected_birth_date &&
          new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(new Date(horse.expected_birth_date))
        }
      />
      <Fact label={t("pub.breeder")} value={horse.breeder} />
    </div>
  );

  const description = localized(horse.description, locale);

  const firstEvent = events?.[0];
  const backTo = firstEvent
    ? { to: eventBase(organizer?.slug, firstEvent.slug), label: localized(firstEvent.title, locale) || t("bid.backToEvent") }
    : organizer?.slug
      ? { to: `/${organizer.slug}`, label: organizer.name }
      : null;

  // Dock móvil persistente: el perfil resuelve caballo→lote abierto (backend)
  // y monta el MISMO BidDock realtime de la página del lote (key = rebind).
  const activeLot = data.active_lot;
  const activeLotUrl = activeLot
    ? `${eventBase(organizer?.slug, activeLot.event_slug || firstEvent?.slug)}/lots/${activeLot.lot_id}`
    : null;

  return (
    <div
      className={`brand-scope ${activeLot ? "pb-24 lg:pb-0" : ""}`}
      style={brandStyle(organizer?.theme)}
      data-testid="horse-profile-page"
    >
      <MicrositeNav organizer={organizer} media={media} backTo={backTo} shareTitle={horse.name} />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24 flex flex-col gap-16">

      {/* ---------- the protagonist: video stage, or the cover if no footage ---------- */}
      {videoFirst ? (
        <div ref={stageRef} className="scroll-mt-24">
          <VideoStage
            videos={stageVideos}
            poster={genetic ? undefined : cover}
            filter={stageFilter}
            onFilterChange={setStageFilter}
            intro={
              organizer?.theme?.horse_video_intro !== false
                ? {
                    horse,
                    logoUrl: organizer?.theme?.logo_media_id ? mediaUrlFromMap(media, organizer.theme.logo_media_id) : null,
                    style: organizer?.theme?.horse_video_intro_style || "dark",
                  }
                : null
            }
          />
        </div>
      ) : (
        cover && (
          <figure className="eq-grain relative rounded-3xl overflow-hidden shadow-[0_16px_40px_rgb(0,0,0,0.08)]">
            <img src={cover} alt={horse.name} className="w-full max-h-[560px] object-cover" loading="eager" />
          </figure>
        )
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start -mt-2">
        <div className="flex flex-col gap-8">
          {titleBlock}
          {description && (
            <div className="flex flex-col gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/50">{t("pub.aboutHorse")}</h2>
              <p className="text-base text-[#0A0A0A]/70 leading-relaxed">{description}</p>
            </div>
          )}
          {horse.notable_siblings && (
            <div className="flex flex-col gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/50">{t("pub.notableSiblings")}</h2>
              <p className="text-sm text-[#0A0A0A]/70 leading-relaxed" data-testid="horse-notable-siblings">{horse.notable_siblings}</p>
            </div>
          )}
          {events.length > 0 && (
            <div className="flex flex-col gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/50">{t("pub.featuredIn")}</h2>
              {events.map((ev) => (
                <Link
                  key={ev.slug}
                  to={`/e/${ev.slug}`}
                  data-testid={`horse-event-link-${ev.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A] hover:text-[#1D4D3B] transition-colors duration-200 w-fit"
                >
                  {localized(ev.title, locale)}
                  <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-6">{factsGrid}</div>
      </div>

      {(horse.sire_info || horse.dam_info) && (
        <section className="flex flex-col gap-8" data-testid="horse-bloodlines">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("pub.bloodlines")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              ["pub.sire", horse.sire_info],
              ["pub.dam", horse.dam_info],
            ].map(
              ([labelKey, info]) =>
                info && (
                  <div key={labelKey} className="bg-[#F9F9F9] rounded-2xl border border-black/5 p-6 lg:p-8 flex flex-col gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.25em]" style={{ color: "var(--brand-accent, #1D4D3B)" }}>
                      {t(labelKey)}
                    </span>
                    <h3 className="text-xl font-medium tracking-tight text-[#0A0A0A]">{info.name}</h3>
                    <p className="text-xs text-[#737373]">
                      {[info.studbook, info.year, info.competition_level].filter(Boolean).join(" · ")}
                    </p>
                    {info.results?.length > 0 && (
                      <ul className="flex flex-col gap-1.5 pt-1">
                        {info.results.map((r, i) => (
                          <li key={i} className="text-sm text-[#0A0A0A]/70">
                            {[r.year, r.competition, r.result].filter(Boolean).join(" — ")}
                          </li>
                        ))}
                      </ul>
                    )}
                    {info.notable_offspring && (
                      <p className="text-sm text-[#737373] leading-relaxed border-t border-black/5 pt-3">
                        <span className="font-medium text-[#0A0A0A]">{t("pub.notableOffspring")}: </span>
                        {info.notable_offspring}
                      </p>
                    )}
                    {(labelKey === "pub.sire" ? sireVideos : damVideos).length > 0 && (
                      <button
                        type="button"
                        data-testid={labelKey === "pub.sire" ? "view-sire-videos" : "view-dam-videos"}
                        onClick={() => jumpToBloodline(labelKey === "pub.sire" ? "sire" : "dam")}
                        className="mt-1 w-fit inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A] hover:text-[#1D4D3B] transition-colors duration-200"
                      >
                        <PlayCircle className="w-4 h-4" aria-hidden="true" />
                        {t(labelKey === "pub.sire" ? "pub.viewSireVideos" : "pub.viewDamVideos")}
                      </button>
                    )}
                  </div>
                )
            )}
          </div>
        </section>
      )}

      {horse.pedigree && (
        <section className="flex flex-col gap-8">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("pub.pedigree")}</h2>
          <PedigreeTree pedigree={horse.pedigree} />
        </section>
      )}

      {/* Fase C — Performance Relatives: junto al pedigree/origen */}
      {horse.performance_relatives?.length > 0 && (
        <section className="flex flex-col gap-8" data-testid="horse-performance-relatives">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("pr.title")}</h2>
          <PerformanceRelatives relatives={horse.performance_relatives} />
        </section>
      )}

      {/* a genetic asset (embryo, breeding right, semen) never shows own results */}
      {!genetic && horse.results?.length > 0 && (
        <section className="flex flex-col gap-8">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("pub.results")}</h2>
          <div className="rounded-2xl border border-black/5 overflow-hidden">
            <Table data-testid="horse-results-table">
              <TableHeader>
                <TableRow className="bg-[#F9F9F9] hover:bg-[#F9F9F9]">
                  <TableHead>{t("ds.tableYear")}</TableHead>
                  <TableHead>{t("ds.tableName")}</TableHead>
                  <TableHead className="text-right">{t("pub.result")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {horse.results.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="tabular-nums text-[#737373]">{r.year}</TableCell>
                    <TableCell className="font-medium">{r.competition}</TableCell>
                    <TableCell className="text-right text-[#1D4D3B] font-medium">{r.result}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      {horse.external_links?.length > 0 && (
        <section className="flex flex-col gap-8" data-testid="horse-external-records">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("pub.officialRecords")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {horse.external_links.map((l, i) => (
              <button
                key={i}
                type="button"
                data-testid={`horse-external-record-${i}`}
                onClick={() => setRecordLink(l)}
                className="group flex items-center gap-4 rounded-2xl border border-black/5 bg-white px-5 py-4 text-left hover:border-black/15 transition-colors duration-200"
              >
                <span className="w-10 h-10 rounded-xl bg-[#1D4D3B]/5 border border-[#1D4D3B]/10 flex items-center justify-center shrink-0">
                  <Globe className="w-[18px] h-[18px] text-[#1D4D3B]" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-[#0A0A0A] truncate">{l.label}</span>
                  <span className="text-xs text-[#737373] truncate">
                    {(() => {
                      try {
                        return new URL(l.url).hostname.replace(/^www\./, "");
                      } catch {
                        return l.url;
                      }
                    })()}
                  </span>
                </span>
                <ArrowUpRight className="w-4 h-4 text-[#0A0A0A]/30 group-hover:text-[#0A0A0A] transition-colors duration-200 shrink-0" aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
      )}

      {documents.length > 0 && (
        <section className="flex flex-col gap-8" data-testid="horse-documents">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("pub.documents")}</h2>
          <ul className="rounded-2xl border border-black/5 overflow-hidden divide-y divide-black/5 bg-white">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center gap-4 px-5 py-4" data-testid={`horse-document-${doc.id}`}>
                <span className="w-10 h-10 rounded-xl bg-[#F9F9F9] border border-black/5 flex items-center justify-center shrink-0">
                  <FileText className="w-[18px] h-[18px] text-[#0A0A0A]/40" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-[#0A0A0A] truncate">{doc.original_filename}</span>
                  <span className="text-xs text-[#737373]">{doc.category ? t(`doccat.${doc.category}`) : t("med.document")}</span>
                </div>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`horse-document-open-${doc.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-black/5 text-sm font-medium text-[#0A0A0A] hover:bg-black/10 transition-colors duration-200"
                >
                  {t("pub.viewDoc")}
                  <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <VetRepository docs={vetDocs} repo={data.vet_repo} onUnlock={reload} horseId={horseId} />

      {photos.length > 0 && (
        <section className="flex flex-col gap-8" data-testid="horse-photo-gallery">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A]">{t("pub.photos")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightboxIndex(i)}
                data-testid={`gallery-photo-${i}`}
                aria-label={`${horse.name} — ${i + 1}`}
                className={`group relative overflow-hidden rounded-2xl border border-black/5 ${i === 0 ? "col-span-2 row-span-2" : ""}`}
              >
                <img
                  src={p.url}
                  alt={p.alt}
                  className={`w-full object-cover transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04] ${
                    i === 0 ? "h-full min-h-[300px] md:min-h-[460px]" : "h-56"
                  }`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {lightboxIndex != null && (
        <Lightbox images={photos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndex={setLightboxIndex} />
      )}
      {recordLink && <ExternalRecordDialog link={recordLink} onClose={() => setRecordLink(null)} />}
      </div>
      {activeLot && <HorseLotDock key={activeLot.lot_id} lotId={activeLot.lot_id} lotUrl={activeLotUrl} />}
    </div>
  );
}
